import { createElement, type ComponentType } from 'react';
import { Grid } from '@material-ui/core';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  EntityGrafanaDashboardsCard,
  EntityGrafanaAlertsCard,
  type AlertsCardOpts,
  type DashboardCardOpts,
  isAlertSelectorAvailable,
  isDashboardSelectorAvailable,
} from '@backstage-community/plugin-grafana';
import { OrgScopedGrafana } from './OrgScopedGrafana';

// "Observability" entity tab. Collects the Grafana Dashboards + Alerts cards
// here, off the Overview tab. The Overview tab is reserved for the catalog
// relation/dependency cards (relations, depends-on-components,
// has-subcomponents, depends-on-resources). The stock grafana cards
// (entity-card:grafana/dashboards, entity-card:grafana/alerts) and our custom
// state-aware alerts card (entity-card:app/grafana-alerts-with-state) are all
// disabled on Overview in app-config.yaml so they render only here.
//
// The tab attaches only to entities carrying a grafana selector annotation
// (dashboard-selector OR alert-label-selector) — same gate the individual
// cards used on Overview — so it never shows an empty tab on other entities.
//
// ORG LINKS: the stock cards build links as `host.domain + path` with no
// orgId, so a viewer whose Grafana session is in another org lands on
// "Dashboard not found". The cards are wrapped in OrgScopedGrafana, which
// swaps grafanaApiRef for a delegate that appends `orgId=<n>` (from
// grafana.hosts[].orgId) to dashboard, folder and alert URLs. The plugin's
// api client class is not exported, so wrapping its instance is the only hook.
//
// RENDER SAFETY: EntityGrafanaDashboardsCard / EntityGrafanaAlertsCard are
// `plugin.provide(createComponentExtension(...))` wrappers typed
// `(opts?) => JSX.Element`; they run React hooks when invoked. They must be
// rendered (createElement defers invocation to React's render), never called
// as plain functions inside the loader callback — calling them there runs
// hooks outside render and crashes the card at runtime (tsc won't catch it).
// Their `(opts?) => JSX.Element` typing isn't a React.FC, so we cast to
// ComponentType<...Opts> to pass opts as props (React invokes the function
// with those props as `opts` at render — same contract). Mirrors
// modules/grafanaAlerts.
const DashboardsCard =
  EntityGrafanaDashboardsCard as unknown as ComponentType<DashboardCardOpts>;
const AlertsCard =
  EntityGrafanaAlertsCard as unknown as ComponentType<AlertsCardOpts>;

const ObservabilityContent = () =>
  createElement(
    OrgScopedGrafana,
    null,
    createElement(
      Grid,
      { container: true, spacing: 3 },
      createElement(
        Grid,
        { item: true, md: 6, xs: 12, key: 'dashboards' },
        createElement(DashboardsCard, {}),
      ),
      createElement(
        Grid,
        { item: true, md: 6, xs: 12, key: 'alerts' },
        createElement(AlertsCard, { showState: true, title: 'Alerts' }),
      ),
    ),
  );

const observabilityContent = EntityContentBlueprint.make({
  name: 'observability',
  params: {
    path: '/observability',
    title: 'Observability',
    filter: (entity: Parameters<typeof isAlertSelectorAvailable>[0]) =>
      Boolean(isDashboardSelectorAvailable(entity)) ||
      isAlertSelectorAvailable(entity),
    loader: async () => createElement(ObservabilityContent),
  },
});

export const observabilityTabModule = createFrontendModule({
  pluginId: 'app',
  extensions: [observabilityContent],
});
